import React, { createContext, useContext, useState, useReducer } from 'react';
import { dateUtils } from '../utils';

const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp debe ser usado dentro de AppProvider');
  }
  return context;
};

// Reducer para manejar notificaciones
const notificationReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_NOTIFICATION':
      return [...state, action.payload];
    case 'REMOVE_NOTIFICATION':
      return state.filter(notification => notification.id !== action.payload);
    case 'CLEAR_NOTIFICATIONS':
      return [];
    default:
      return state;
  }
};

export const AppProvider = ({ children }) => {
  // Estados generales de la aplicación
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [theme, setTheme] = useState('light');
  const [notifications, notificationDispatch] = useReducer(notificationReducer, []);
  const [loading, setLoading] = useState(false);
  const [currentAcademicYear, setCurrentAcademicYear] = useState('2024');

  // Configuración de la aplicación
  const [appConfig, setAppConfig] = useState({
    schoolName: 'Subercaseaux College',
    schoolLogo: '/logo.png',
    primaryColor: '#0ea5e9',
    language: 'es',
    timezone: 'America/Santiago',
    features: {
      messaging: true,
      calendar: true,
      finances: true,
      evaluations: true,
      reports: true
    }
  });

  // Funciones para manejar el sidebar
  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const closeSidebar = () => setSidebarOpen(false);
  const openSidebar = () => setSidebarOpen(true);

  // Funciones para manejar notificaciones
  const addNotification = (notification) => {
    const id = `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    notificationDispatch({
      type: 'ADD_NOTIFICATION',
      payload: {
        ...notification,
        id,
        timestamp: new Date(),
        read: false
      }
    });

    // Auto-remove notification after 5 seconds if it's not persistent
    if (!notification.persistent) {
      setTimeout(() => {
        removeNotification(id);
      }, 5000);
    }
  };

  const removeNotification = (id) => {
    notificationDispatch({
      type: 'REMOVE_NOTIFICATION',
      payload: id
    });
  };

  const clearAllNotifications = () => {
    notificationDispatch({ type: 'CLEAR_NOTIFICATIONS' });
  };

  const showSuccess = (message, title = 'Éxito') => {
    addNotification({
      type: 'success',
      title,
      message,
      persistent: false
    });
  };

  const showError = (message, title = 'Error') => {
    addNotification({
      type: 'error',
      title,
      message,
      persistent: true
    });
  };

  const showWarning = (message, title = 'Advertencia') => {
    addNotification({
      type: 'warning',
      title,
      message,
      persistent: false
    });
  };

  const showInfo = (message, title = 'Información') => {
    addNotification({
      type: 'info',
      title,
      message,
      persistent: false
    });
  };

  // Función para cambiar el tema
  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  };

  // Función para actualizar la configuración
  const updateAppConfig = (newConfig) => {
    setAppConfig(prevConfig => ({ ...prevConfig, ...newConfig }));
  };

  // Función para manejar loading global
  const setGlobalLoading = (isLoading) => {
    setLoading(isLoading);
  };

  // Estados para modales globales
  const [modals, setModals] = useState({});

  const openModal = (modalId, props = {}) => {
    setModals(prev => ({
      ...prev,
      [modalId]: { isOpen: true, props }
    }));
  };

  const closeModal = (modalId) => {
    setModals(prev => ({
      ...prev,
      [modalId]: { isOpen: false, props: {} }
    }));
  };

  const isModalOpen = (modalId) => {
    return modals[modalId]?.isOpen || false;
  };

  const getModalProps = (modalId) => {
    return modals[modalId]?.props || {};
  };

  // Estados para breadcrumbs
  const [breadcrumbs, setBreadcrumbs] = useState([]);

  const updateBreadcrumbs = (newBreadcrumbs) => {
    setBreadcrumbs(newBreadcrumbs);
  };

  // Función de utilidad para formatear fechas
  const formatDate = (date, format = 'dd/MM/yyyy') => {
    if (!date) return '';
    const d = new Date(date);
    
    switch (format) {
      case 'dd/MM/yyyy':
        return d.toLocaleDateString('es-CL');
      case 'dd/MM/yyyy HH:mm':
        return d.toLocaleString('es-CL');
      case 'relative':
        return dateUtils.getRelativeTime(d);
      default:
        return d.toLocaleDateString('es-CL');
    }
  };

  const getRelativeTime = (date) => {
    const now = new Date();
    const diffInMs = now - date;
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInMinutes < 1) return 'Ahora';
    if (diffInMinutes < 60) return `Hace ${diffInMinutes} minutos`;
    if (diffInHours < 24) return `Hace ${diffInHours} horas`;
    if (diffInDays < 7) return `Hace ${diffInDays} días`;
    return formatDate(date);
  };

  const value = {
    // Estados
    sidebarOpen,
    theme,
    notifications,
    loading,
    currentAcademicYear,
    appConfig,
    breadcrumbs,
    
    // Funciones de sidebar
    toggleSidebar,
    closeSidebar,
    openSidebar,
    
    // Funciones de notificaciones
    addNotification,
    removeNotification,
    clearAllNotifications,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    
    // Funciones de configuración
    toggleTheme,
    updateAppConfig,
    setGlobalLoading,
    setCurrentAcademicYear,
    updateBreadcrumbs,
    
    // Funciones de modales
    openModal,
    closeModal,
    isModalOpen,
    getModalProps,
    
    // Utilidades
    formatDate,
    getRelativeTime
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};